import { SvgIcon } from "@progress/kendo-react-common";
import { caretAltDownIcon } from "@progress/kendo-svg-icons";

type MultiSelectToggleButtonProps = {
  opened: boolean;
  onToggle: () => void;
  disabled?: boolean;
};

/**
 * Kendo's MultiSelect has no visible dropdown-arrow button by default
 * (unlike DropDownList/ComboBox), so there's no way to browse the full
 * option list without typing something first - keyboard users have no
 * discoverable way in (the only keyboard shortcut that opens it, Alt+Down,
 * isn't documented on screen anywhere). Pass this to a MultiSelect's
 * `suffix` prop (alongside controlled `opened`/`onOpen`/`onClose`) to add a
 * real, focusable button that does the same job.
 * Pass `disabled` whenever the MultiSelect itself is disabled/read-only:
 * Kendo's own `disabled` prop has no effect on elements rendered through
 * `suffix` (they're ours, not Kendo's), so without this a "disabled" field's
 * toggle would still be fully operable by keyboard.
 */
export function MultiSelectToggleButton({
  opened,
  onToggle,
  disabled,
}: MultiSelectToggleButtonProps) {
  return (
    <button
      type="button"
      className="multiselect-toggle-button"
      aria-label={opened ? "Close options list" : "Open options list"}
      aria-haspopup="listbox"
      aria-expanded={opened}
      disabled={disabled}
      onMouseDown={(e) => {
        // Kendo's own input blurs (and would close the popup) on mousedown
        // outside of it, before this button's onClick fires. Only prevent
        // that default here - the actual toggle happens in onClick below, so
        // it fires exactly once per activation, by mouse or by keyboard
        // (Enter/Space on a real, focusable button - no mousedown involved).
        e.preventDefault();
      }}
      onClick={onToggle}
    >
      <SvgIcon icon={caretAltDownIcon} aria-hidden="true" />
    </button>
  );
}
